import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Upload, FileText, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookForm = ({ book, onSave, onCancel, token }) => {
  const [form, setForm] = useState(book || {
    title: '',
    author: 'Dior Russell',
    description: '',
    price: '',
    cover_image: '',
    category: '',
    isbn: '',
    pages: '',
    published_date: '',
    is_featured: false,
    is_new_release: false,
    stock: 100,
    preview_content: '',
    embed_code: '',
    download_enabled: true,
    embed_enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [fileInfo, setFileInfo] = useState(
    book?.file_name ? { name: book.file_name, size: book.file_size } : null
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({
        ...form,
        price: parseFloat(form.price),
        pages: form.pages ? parseInt(form.pages) : null,
        stock: parseInt(form.stock),
        embed_code: form.embed_code || null,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save book');
    }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!book?.book_id) {
      toast.error('Save the book first, then upload a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/books/${book.book_id}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setFileInfo({ name: res.data.file_name, size: res.data.file_size });
      toast.success('Book file uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleRemoveFile = async () => {
    if (!book?.book_id) return;
    try {
      await axios.delete(`${API}/admin/books/${book.book_id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFileInfo(null);
      toast.success('File removed');
    } catch (error) {
      toast.error('Failed to remove file');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="book-form-title" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="author">Author *</Label>
          <Input id="author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required data-testid="book-form-author" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} required />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Price ($) *</Label>
          <Input id="price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Input id="category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Cover Image</Label>
        {form.cover_image ? (
          <div className="flex items-center gap-3 p-3 border border-border rounded-sm bg-muted/30">
            <img src={form.cover_image} alt="Cover" className="w-16 h-24 object-cover rounded-sm" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="flex-1">
              <p className="text-sm font-medium">Cover image set</p>
              <p className="text-xs text-muted-foreground truncate max-w-[300px]">{form.cover_image}</p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => setForm({ ...form, cover_image: '' })} className="text-destructive">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setUploadingCover(true);
                  const formData = new FormData();
                  formData.append('file', file);
                  try {
                    const res = await axios.post(`${API}/admin/upload-cover`, formData, {
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                    });
                    setForm(prev => ({ ...prev, cover_image: res.data.url }));
                    toast.success('Cover image uploaded');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Upload failed');
                  }
                  setUploadingCover(false);
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploadingCover}
                data-testid="cover-image-upload"
              />
              <div className={`flex items-center justify-center gap-2 p-6 border-2 border-dashed border-border rounded-sm hover:border-primary cursor-pointer transition-colors`}>
                {uploadingCover ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
                <span className="text-sm text-muted-foreground">
                  {uploadingCover ? 'Uploading...' : 'Click to upload cover image'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or paste URL</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <Input
              value={form.cover_image}
              onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="https://example.com/cover.jpg"
              data-testid="cover-image-url"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="isbn">ISBN</Label>
          <Input id="isbn" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pages">Pages</Label>
          <Input id="pages" type="number" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="stock">Stock *</Label>
          <Input id="stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="published_date">Published Date</Label>
        <Input id="published_date" type="date" value={form.published_date} onChange={(e) => setForm({ ...form, published_date: e.target.value })} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="preview_content">Preview Content (Book Preview/Sample)</Label>
        <Textarea id="preview_content" value={form.preview_content || ''} onChange={(e) => setForm({ ...form, preview_content: e.target.value })} placeholder="Add a sample chapter or excerpt..." rows={4} />
      </div>

      {/* Embed Code */}
      <div className="space-y-2">
        <Label htmlFor="embed_code">Embed Code (iframe/HTML)</Label>
        <Textarea
          id="embed_code"
          value={form.embed_code || ''}
          onChange={(e) => setForm({ ...form, embed_code: e.target.value })}
          placeholder='<iframe src="https://..." width="100%" height="600"></iframe>'
          rows={3}
          className="font-mono text-sm"
          data-testid="book-form-embed-code"
        />
        <p className="text-xs text-muted-foreground">Paste embed code from Google Docs, Issuu, FlipHTML5, etc. Users will see this in their library.</p>
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Label>Book File (PDF, EPUB, etc.)</Label>
        {fileInfo ? (
          <div className="flex items-center gap-3 p-3 border border-border rounded-sm bg-muted/30">
            <FileText className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">{fileInfo.name}</p>
              <p className="text-xs text-muted-foreground">{formatSize(fileInfo.size)}</p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={handleRemoveFile} className="text-destructive">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="file"
              accept=".pdf,.epub,.mobi,.azw3,.doc,.docx,.txt"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={!book?.book_id || uploading}
              data-testid="book-file-upload"
            />
            <div className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-border rounded-sm ${!book?.book_id ? 'opacity-50' : 'hover:border-primary cursor-pointer'} transition-colors`}>
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
              <span className="text-sm text-muted-foreground">
                {uploading ? 'Uploading...' : !book?.book_id ? 'Save book first to upload file' : 'Click to upload book file'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Toggles */}
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch id="is_featured" checked={form.is_featured} onCheckedChange={(checked) => setForm({ ...form, is_featured: checked })} />
          <Label htmlFor="is_featured">Featured</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="is_new_release" checked={form.is_new_release} onCheckedChange={(checked) => setForm({ ...form, is_new_release: checked })} />
          <Label htmlFor="is_new_release">New Release</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="download_enabled" checked={form.download_enabled ?? true} onCheckedChange={(checked) => setForm({ ...form, download_enabled: checked })} />
          <Label htmlFor="download_enabled">Download Enabled</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="embed_enabled" checked={form.embed_enabled ?? true} onCheckedChange={(checked) => setForm({ ...form, embed_enabled: checked })} />
          <Label htmlFor="embed_enabled">Embed Enabled</Label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={loading} data-testid="book-form-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {book ? 'Update' : 'Create'} Book
        </Button>
      </div>
    </form>
  );
};

export const AdminBooksPage = () => {
  const { token } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books?limit=100`, { headers: getHeaders(), withCredentials: true });
      setBooks(response.data.books || []);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchBooks(); }, [token]);

  const handleCreate = async (bookData) => {
    await axios.post(`${API}/admin/books`, bookData, { headers: getHeaders(), withCredentials: true });
    toast.success('Book created successfully');
    setDialogOpen(false);
    fetchBooks();
  };

  const handleUpdate = async (bookData) => {
    await axios.put(`${API}/admin/books/${editingBook.book_id}`, bookData, { headers: getHeaders(), withCredentials: true });
    toast.success('Book updated successfully');
    setDialogOpen(false);
    setEditingBook(null);
    fetchBooks();
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book?')) return;
    try {
      await axios.delete(`${API}/admin/books/${bookId}`, { headers: getHeaders(), withCredentials: true });
      toast.success('Book deleted');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  const openEditDialog = (book) => {
    setEditingBook(book);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBook(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-books">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl font-bold">Books</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-lift" onClick={() => setEditingBook(null)} data-testid="add-book-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Book
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingBook ? 'Edit Book' : 'Add New Book'}
              </DialogTitle>
            </DialogHeader>
            <BookForm
              book={editingBook}
              onSave={editingBook ? handleUpdate : handleCreate}
              onCancel={closeDialog}
              token={token}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Book</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {books.map((book) => (
                <TableRow key={book.book_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img src={book.cover_image} alt={book.title} className="w-10 h-14 object-cover rounded-sm" />
                      <div>
                        <p className="font-medium">{book.title}</p>
                        <p className="text-sm text-muted-foreground">{book.author}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{book.category}</TableCell>
                  <TableCell>${book.price.toFixed(2)}</TableCell>
                  <TableCell>{book.stock}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {book.file_name && (
                        <Badge variant="outline" className="text-xs">
                          <FileText className="h-3 w-3 mr-1" />
                          File
                        </Badge>
                      )}
                      {book.embed_code && (
                        <Badge variant="outline" className="text-xs">Embed</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {book.is_featured && <Badge>Featured</Badge>}
                      {book.is_new_release && <Badge variant="secondary">New</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(book)} data-testid={`edit-book-${book.book_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(book.book_id)} className="text-destructive hover:text-destructive" data-testid={`delete-book-${book.book_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
